import React from 'react';
import { Clock, ShieldCheck, DollarSign, Award, Wrench, UserCheck, FileText, Umbrella, Star, Calendar, MapPin, Phone } from 'lucide-react';
import { motion } from 'framer-motion';
function CheckCircleIcon(props: any) {
  return <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-493">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" data-id="element-494"></path>
      <polyline points="22 4 12 14.01 9 11.01" data-id="element-495"></polyline>
    </svg>;
}
export function WhyChoose() {
  const reasons = [{
    icon: ShieldCheck,
    title: 'Licensed, insured, ABN-registered',
    desc: 'ABN 40 434 878 561. EC #xxxxx (to confirm). Public Liability $20M cover. Full compliance with WA EnergySafety on every job.',
    stat: 'Verified credentials',
    num: '01'
  }, {
    icon: Clock,
    title: "Punctual or it's on us",
    desc: 'We arrive when we say we will. Same-day for emergencies, booked within 48 hours for standard jobs.',
    stat: 'Same-day emergencies',
    num: '02'
  }, {
    icon: DollarSign,
    title: 'No Bill Shock. Ever.',
    desc: "You'll know the full cost before we start. No hidden fees, no surprise extras. The price we quote is the price you pay.",
    stat: 'Upfront fixed pricing',
    num: '03'
  }, {
    icon: UserCheck,
    title: 'Quality Over Shortcuts',
    desc: 'We attract customers who value quality workmanship over the cheapest price. Every job is done right the first time.',
    stat: '441 five-star Google reviews',
    num: '04'
  }, {
    icon: Wrench,
    title: 'The Completely Electrical Guarantee',
    desc: "Every job is backed by our workmanship guarantee. If you're not 100% satisfied, we'll make it right.",
    stat: 'Guaranteed satisfaction',
    num: '05'
  }, {
    icon: Award,
    title: '10+ Years of Local Experience',
    desc: 'Brodie and the team have served Perth South since 2020, with over a decade of combined trade experience behind every job.',
    stat: 'Trading since 2020',
    num: '06'
  }];
  const credentials = [{
    icon: ShieldCheck,
    label: 'License',
    value: 'EC #xxxxx (WA Electrical Contractor)'
  }, {
    icon: FileText,
    label: 'ABN',
    value: '40 434 878 561'
  }, {
    icon: Umbrella,
    label: 'Public Liability',
    value: '$20M cover (TBC)'
  }, {
    icon: Star,
    label: 'Google Rating',
    value: '5.0 across 441 reviews'
  }, {
    icon: Calendar,
    label: 'Trading Since',
    value: '2020'
  }, {
    icon: MapPin,
    label: 'Based In',
    value: 'Huntingdale WA 6110'
  }];
  return <section className="py-20 lg:py-28 bg-white relative overflow-hidden" data-id="element-496">
      <div className="max-w-7xl mx-auto px-4 relative z-10" data-id="element-497">
        <div className="text-center max-w-3xl mx-auto mb-14" data-id="element-498">
          <span className="text-accent font-bold tracking-wider uppercase text-sm mb-3 block" data-id="element-499">
            The Completely Electrical Difference
          </span>
          <h2 className="text-4xl md:text-5xl font-heading font-extrabold text-navy mb-6 tracking-tighter" data-id="element-500">
            What You Can Expect When You Choose Us
          </h2>
          <p className="text-lg text-slate leading-relaxed" data-id="element-501">
            Had a bad experience with another electrician? You're not alone. We
            built Completely Electrical to be the electrician we'd want to hire
            ourselves: punctual, honest, and genuinely good at the job.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start mb-14" data-id="element-502">
          {/* Left: Reason Cards */}
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5" data-id="element-503">
            {reasons.map((reason, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: index * 0.08
          }} className="bg-white border border-gray-100 p-5 rounded-2xl shadow-sm hover:shadow-xl transition-all relative overflow-hidden group" data-id="element-504">
                <div className="absolute -top-4 -right-2 text-7xl font-heading font-extrabold text-gray-50 opacity-50 group-hover:text-accent/5 transition-colors pointer-events-none select-none" data-id="element-505">
                  {reason.num}
                </div>

                <div className="w-11 h-11 bg-accent/10 rounded-full flex items-center justify-center mb-3 relative z-10 group-hover:scale-110 transition-transform" data-id="element-506">
                  <reason.icon className="w-5 h-5 text-accent" data-id="element-507" />
                </div>

                <h3 className="text-base font-heading font-extrabold text-navy mb-1.5 relative z-10" data-id="element-508">
                  {reason.title}
                </h3>
                <p className="text-slate text-sm leading-relaxed mb-3 relative z-10" data-id="element-509">
                  {reason.desc}
                </p>

                <div className="inline-flex items-center gap-1.5 bg-cream px-2.5 py-1 rounded-lg text-xs font-bold text-navy relative z-10" data-id="element-510">
                  <CheckCircleIcon className="w-3 h-3 text-emerald-500" data-id="element-511" />
                  {reason.stat}
                </div>
              </motion.div>)}
          </div>

          {/* Right: Credentials Card */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="lg:col-span-5 relative lg:sticky lg:top-32" data-id="element-512">
            <div className="absolute -top-6 -right-6 w-32 h-32 bg-accent/10 rounded-full z-0 blur-2xl" data-id="element-513"></div>
            <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-cream rounded-full z-0 blur-2xl" data-id="element-514"></div>

            <div className="relative z-10 bg-white border-2 border-accent/20 rounded-2xl shadow-xl overflow-hidden" data-id="element-515">
              {/* Certificate-style top strip */}
              <div className="bg-accent/10 border-b border-accent/20 px-6 py-2 flex items-center justify-between text-[10px] font-bold tracking-[0.15em] uppercase text-navy/70" data-id="element-516">
                <span data-id="element-517">Cert. No. CE-WA-2024</span>
                <span className="hidden sm:inline" data-id="element-518">Completely Electrical</span>
                <span data-id="element-519">Issued 2024</span>
              </div>

              {/* Header */}
              <div className="bg-navy px-6 py-5 border-b border-white/10 relative overflow-hidden" data-id="element-520">
                {/* Subtle embossed seal in corner */}
                <div className="absolute -right-4 -bottom-4 w-24 h-24 rounded-full border-2 border-accent/20 flex items-center justify-center pointer-events-none" data-id="element-521">
                  <div className="w-16 h-16 rounded-full border border-accent/15 flex items-center justify-center" data-id="element-522">
                    <ShieldCheck className="w-7 h-7 text-accent/20" data-id="element-523" />
                  </div>
                </div>

                <div className="inline-flex items-center gap-2 bg-accent/20 border border-accent/30 px-3 py-1 rounded-full mb-3 relative z-10" data-id="element-524">
                  <ShieldCheck className="w-3.5 h-3.5 text-accent" data-id="element-525" />
                  <span className="text-accent text-[11px] font-extrabold uppercase tracking-wider" data-id="element-526">
                    Verified Credentials
                  </span>
                </div>
                <h3 className="text-xl font-heading font-bold text-white leading-tight relative z-10" data-id="element-527">
                  Everything you need to trust us, in one place.
                </h3>
              </div>

              {/* Credentials list */}
              <div className="divide-y divide-gray-100" data-id="element-528">
                {credentials.map((cred, index) => <motion.div key={index} initial={{
                opacity: 0,
                x: 10
              }} whileInView={{
                opacity: 1,
                x: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.3,
                delay: 0.3 + index * 0.08
              }} className="flex items-center gap-4 px-6 py-4 hover:bg-cream/50 transition-colors" data-id="element-529">
                    <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-530">
                      <cred.icon className="w-5 h-5 text-accent" data-id="element-531" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-532">
                      <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-0.5" data-id="element-533">
                        {cred.label}
                      </div>
                      <div className="text-sm font-extrabold text-navy truncate" data-id="element-534">
                        {cred.value}
                      </div>
                    </div>
                  </motion.div>)}
              </div>

              {/* Guarantee badge */}
              <div className="bg-cream border-t border-gray-100 px-6 py-5" data-id="element-535">
                <div className="flex items-start gap-3" data-id="element-536">
                  <div className="w-9 h-9 bg-accent rounded-full flex items-center justify-center flex-shrink-0" data-id="element-537">
                    <ShieldCheck className="w-5 h-5 text-white" data-id="element-538" />
                  </div>
                  <div data-id="element-539">
                    <div className="text-sm font-extrabold text-navy mb-0.5" data-id="element-540">
                      The Completely Electrical Guarantee
                    </div>
                    <div className="text-xs text-slate leading-relaxed" data-id="element-541">
                      Quality workmanship, every time. Or we make it right.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="flex justify-center" data-id="element-542">
          <a href="tel:0418381988" className="bg-navy hover:bg-navy-600 text-white px-10 py-5 rounded-xl font-extrabold text-lg flex items-center justify-center gap-3 transition-all shadow-lg hover:-translate-y-1" data-id="element-543">
            <Phone className="w-6 h-6 text-accent" data-id="element-544" />
            Experience the Difference. Call 0418 381 988
          </a>
        </div>
      </div>
    </section>;
}